import { useState, useCallback, useRef, useEffect } from "react";
import { productsService } from "../services/productsService";

// Hook para manejar sugerencias de búsqueda con autocompletado
const useSearchSuggestions = (filterOptions = {}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  // Generar sugerencias desde las opciones de filtros como fallback
  const generateFallbackSuggestions = useCallback((query, options) => {
    if (!query || query.length < 2) return [];

    const searchTerm = query.toLowerCase();
    const fallbackSuggestions = [];

    // Buscar en categorías
    if (options.categories?.length) {
      options.categories
        .filter((cat) => cat.toLowerCase().includes(searchTerm))
        .slice(0, 3)
        .forEach((category) => {
          fallbackSuggestions.push({
            type: "category",
            value: category,
            count: Math.floor(Math.random() * 50) + 10, // Número estimado
            category: null,
          });
        });
    }

    // Buscar en marcas
    if (options.brands?.length) {
      options.brands
        .filter((brand) => brand.toLowerCase().includes(searchTerm))
        .slice(0, 3)
        .forEach((brand) => {
          fallbackSuggestions.push({
            type: "brand",
            value: brand,
            count: Math.floor(Math.random() * 30) + 5,
            category: null,
          });
        });
    }

    // Buscar en modelos
    if (options.models?.length) {
      options.models
        .filter((model) => model.toLowerCase().includes(searchTerm))
        .slice(0, 3)
        .forEach((model) => {
          fallbackSuggestions.push({
            type: "model",
            value: model,
            count: Math.floor(Math.random() * 20) + 3,
            category: null,
          });
        });
    }

    // Buscar en ubicaciones
    if (options.locations?.length) {
      options.locations
        .filter((location) => location.toLowerCase().includes(searchTerm))
        .slice(0, 2)
        .forEach((location) => {
          fallbackSuggestions.push({
            type: "location",
            value: location,
            count: Math.floor(Math.random() * 40) + 8,
            category: null,
          });
        });
    }

    // Ordenar por relevancia (coincidencia exacta primero)
    return fallbackSuggestions
      .sort((a, b) => {
        const aExact = a.value.toLowerCase().startsWith(searchTerm);
        const bExact = b.value.toLowerCase().startsWith(searchTerm);

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        return b.count - a.count; // Por cantidad descendente
      })
      .slice(0, 8); // Máximo 8 sugerencias
  }, []);

  // Obtener sugerencias de la API o fallback
  const fetchSuggestions = useCallback(
    async (query) => {
      // Cancelar búsqueda anterior si existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!query || query.trim().length < 2) {
        setSuggestions([]);
        setError(null);
        return;
      }

      // Crear nuevo AbortController
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        console.log("🔍 Fetching suggestions for:", query);

        // Intentar obtener sugerencias de la API
        const response = await productsService.getSearchSuggestions(
          query.trim(),
          { limit: 8 },
          null // token - por ahora null, se puede agregar si es necesario
        );

        // Verificar si la request fue cancelada
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        if (response.suggestions && response.suggestions.length > 0) {
          console.log(
            "✅ API suggestions received:",
            response.suggestions.length
          );
          setSuggestions(response.suggestions);
        } else if (response.fallback || response.suggestions.length === 0) {
          // Usar fallback si la API no devuelve resultados o falló
          console.log("🔄 Using fallback suggestions");
          const fallbackSuggestions = generateFallbackSuggestions(
            query,
            filterOptions
          );
          setSuggestions(fallbackSuggestions);
        }
      } catch (error) {
        // Verificar si la request fue cancelada
        if (abortControllerRef.current?.signal.aborted) {
          return;
        }

        console.log(
          "⚠️ API suggestions failed, using fallback:",
          error.message
        );

        // Usar fallback en caso de error
        const fallbackSuggestions = generateFallbackSuggestions(
          query,
          filterOptions
        );
        setSuggestions(fallbackSuggestions);

        // Solo setear error si no hay sugerencias de fallback
        if (fallbackSuggestions.length === 0) {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    },
    [filterOptions, generateFallbackSuggestions]
  );

  // Limpiar sugerencias
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);

    // Cancelar búsqueda en progreso
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    suggestions,
    loading,
    error,
    fetchSuggestions,
    clearSuggestions,
  };
};

export default useSearchSuggestions;
